import React, {useState, useEffect, useCallback} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useAPI} from '../../config/API'
import UserImageCircle from '../../components/UserImageCircle'
import User from '../../models/User'
import SystemNotification from '../../models/SystemNotification'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const cx = (...classes) => classes.filter(Boolean).join(' ')

const fmt = (iso) =>
    new Date(iso).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    })

const senderInitials = (name) =>
    (name || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()

// ─── Small atoms ─────────────────────────────────────────────────────────────

const Badge = ({label, cls}) => (
    <span className={cx('inline-flex items-center text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap', cls)}>
        {label}
    </span>
)

const SectionLabel = ({children}) => (
    <div className="text-[10.5px] font-bold uppercase tracking-widest text-gray-400 mb-3">
        {children}
    </div>
)

const MetaItem = ({icon, label, children}) => (
    <div className="flex items-start gap-2.5">
        <span className="mt-0.5 shrink-0 text-gray-400">{icon}</span>
        <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
            <div className="text-[13px] text-gray-700">{children}</div>
        </div>
    </div>
)

// ─── Icons ───────────────────────────────────────────────────────────────────

const IconClock    = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
const IconUsers    = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
const IconMail     = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>
const IconTag      = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7" cy="7" r="1" fill="currentColor"/></svg>
const IconFilter   = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/></svg>
const IconLocation = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
const IconBriefcase = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>

// ─── UserAvatar (for recipient rows) ─────────────────────────────────────────

const AVATAR_COLORS = ['#4F54E8', '#0F766E', '#6D28D9', '#065F46', '#92400E', '#1D4ED8', '#0369A1', '#7C3AED']

const UserAvatar = ({user, size = 32}) => {
    const style = {width: size, height: size, fontSize: size * 0.34}
    if (user.image_path) {
        return <img src={user.image_path} alt={user.name} className="rounded-full object-cover shrink-0" style={style}/>
    }
    return (
        <div
            className="rounded-full flex items-center justify-center font-bold text-white shrink-0"
            style={{...style, background: AVATAR_COLORS[user.id % AVATAR_COLORS.length]}}
        >
            {senderInitials(user.name)}
        </div>
    )
}

// ─── AdminMessage ─────────────────────────────────────────────────────────────

const AdminMessage = () => {
    const {messageId} = useParams()
    const navigate    = useNavigate()

    const {data, isLoading, isError} = useAPI(
        ['fetchSystemNotification', {messageId}],
        {enabled: !!messageId}
    )

    const [recipients, setRecipients]       = useState([])
    const [recipientsMeta, setRecipientsMeta] = useState({current_page: 1, last_page: 1})
    const [loadingMore, setLoadingMore]     = useState(false)

    useEffect(() => {
        if (!data?.user_notifications) return
        const paginated = data.user_notifications
        setRecipients((paginated.data ?? []).map(n => ({...n, user: User.make(n.user)})))
        setRecipientsMeta({
            current_page: paginated.current_page ?? 1,
            last_page:    paginated.last_page    ?? 1,
        })
    }, [data])

    const loadMore = useCallback(async () => {
        const nextPage = recipientsMeta.current_page + 1
        setLoadingMore(true)
        try {
            const response  = await TB.request(
                TB.api_endpoint(`/api/system-notifications/${messageId}?notifications_page=${nextPage}`)
            ).get({})
            const paginated = response?.success?.data?.notification?.user_notifications
            if (!paginated) return
            setRecipients(prev => [...prev, ...(paginated.data ?? []).map(n => ({...n, user: User.make(n.user)}))])
            setRecipientsMeta({current_page: paginated.current_page, last_page: paginated.last_page})
        } finally {
            setLoadingMore(false)
        }
    }, [messageId, recipientsMeta.current_page])

    // ─── Loading ─────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex flex-col w-full min-h-screen bg-gray-100">
                <div className="px-7 py-5 bg-black shrink-0">
                    <div className="h-5 bg-gray-700 rounded w-48 animate-pulse"/>
                </div>
                <div className="py-8 px-7 max-w-3xl mx-auto w-full space-y-4">
                    <div className="grid grid-cols-3 gap-3.5">
                        {[1,2,3].map(i => <div key={i} className="h-20 bg-white border border-gray-200 animate-pulse"/>)}
                    </div>
                    <div className="bg-white border border-gray-200 p-8 space-y-3 animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-3/4"/>
                        <div className="h-4 bg-gray-100 rounded w-1/3"/>
                        <div className="h-px bg-gray-100 my-2"/>
                        <div className="h-3.5 bg-gray-100 rounded"/>
                        <div className="h-3.5 bg-gray-100 rounded w-5/6"/>
                        <div className="h-3.5 bg-gray-100 rounded w-4/6"/>
                    </div>
                </div>
            </div>
        )
    }

    // ─── Error / not found ────────────────────────────────────────────────────
    if (isError || (!isLoading && !data)) {
        return (
            <div className="flex flex-col w-full min-h-screen bg-gray-100">
                <div className="px-7 py-5 bg-black flex items-center gap-4 shrink-0">
                    <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white transition-colors">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <polyline points="15,18 9,12 15,6"/>
                        </svg>
                    </button>
                    <h1 className="text-lg font-bold tracking-tight text-white">Message Not Found</h1>
                </div>
                <div className="py-16 text-center text-gray-500">
                    <p className="text-[13px]">Message #{messageId} could not be found.</p>
                    <button onClick={() => navigate('../messaging')} className="mt-4 text-indigo-600 text-[13px] font-semibold hover:underline">
                        ← Back to Sent Messages
                    </button>
                </div>
            </div>
        )
    }

    // ─── Derived display values ───────────────────────────────────────────────
    const message       = data
    const category      = SystemNotification.category(message.category)
    const action        = SystemNotification.action(message.action, message.category)
    const audienceLabel = SystemNotification.audienceLabel(message)
    const filters       = message.filters || {}
    const isClientCategory = SystemNotification.CLIENT_CATEGORIES.includes(message.category)

    const disciplineNames = (filters.discipline_ids ?? [])
        .map(id => SystemNotification.disciplineName(id))
        .filter(Boolean)

    const excludedCount = filters.exluded_user_ids?.length ?? 0
    const addedCount    = filters.added_user_ids?.length   ?? 0

    const hasFilters = disciplineNames.length > 0
        || filters.state
        || filters.city
        || excludedCount > 0
        || addedCount > 0

    // ─── View ─────────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col w-full min-h-screen bg-gray-100 text-gray-900 text-sm">

            {/* Header */}
            <div className="px-7 py-5 bg-black flex items-center gap-4 shrink-0">
                <button
                    onClick={() => navigate('../messaging')}
                    className="text-gray-400 hover:text-white transition-colors shrink-0"
                    title="Back to Sent Messages"
                >
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="15,18 9,12 15,6"/>
                    </svg>
                </button>
                <div className="min-w-0">
                    <h1 className="text-lg font-bold tracking-tight text-white truncate">{message.title}</h1>
                    <p className="text-[13px] text-gray-400 mt-0.5">Sent {fmt(message.created_at)}</p>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 py-7 px-7">
                <div className="max-w-3xl mx-auto flex flex-col gap-4">

                    {/* ── Stats strip ── */}
                    <div className="grid grid-cols-3 gap-3.5">
                        {[
                            {label: 'Recipients', value: message.user_notifications_count, accent: 'border-indigo-500',  valueColor: 'text-indigo-600'},
                            {label: 'Read',       value: message.read_count,               accent: 'border-emerald-500', valueColor: 'text-emerald-600'},
                            {label: 'Unread',     value: message.unread_count,             accent: 'border-gray-300',    valueColor: 'text-gray-500'},
                        ].map(({label, value, accent, valueColor}) => (
                            <div key={label} className={cx('bg-white border border-gray-200 border-l-4 p-4', accent)}>
                                <div className="text-[10.5px] font-bold uppercase tracking-widest text-gray-400 mb-1">{label}</div>
                                <div className={cx('text-3xl font-bold tracking-tight', valueColor)}>{value ?? '—'}</div>
                            </div>
                        ))}
                    </div>

                    {/* ── Message card ── */}
                    <div className="bg-white border border-gray-200">

                        {/* Badges */}
                        <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center flex-wrap gap-2">
                            <Badge {...category}/>
                            <Badge {...action}/>
                            {message.is_urgent && (
                                <Badge label="High Priority" cls="bg-red-100 text-red-600"/>
                            )}
                        </div>

                        {/* Title */}
                        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
                            <h2 className="text-xl font-bold tracking-tight">{message.title}</h2>
                            <p className="text-[12px] text-gray-400 mt-1">
                                #{message.id} · Last updated {fmt(message.updated_at)}
                            </p>
                        </div>

                        {/* Body HTML */}
                        <div
                            className="px-6 py-6 prose prose-sm max-w-none text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{__html: message.body}}
                        />
                    </div>

                    {/* ── Sender + Delivery (two columns) ── */}
                    <div className="grid grid-cols-2 gap-4">

                        {/* Sender card */}
                        <div className="bg-white border border-gray-200 p-5">
                            <SectionLabel>Sent By</SectionLabel>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-[13px] shrink-0">
                                    {senderInitials(message.sender_name)}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[14px] font-semibold text-gray-900 truncate">
                                        {message.sender_name}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[12px] text-gray-400 mt-0.5 min-w-0">
                                        <IconMail/>
                                        <span className="truncate">{message.sender_email}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Delivery card */}
                        <div className="bg-white border border-gray-200 p-5 flex flex-col gap-3.5">
                            <SectionLabel>Delivery</SectionLabel>

                            <MetaItem icon={<IconTag/>} label="Category">
                                <Badge {...category}/>
                            </MetaItem>

                            <MetaItem icon={<IconUsers/>} label="Audience">
                                {audienceLabel}
                            </MetaItem>

                            {isClientCategory && message.subject_id && (
                                <MetaItem icon={<IconBriefcase/>} label="Role (Position)">
                                    Position #{message.subject_id}
                                </MetaItem>
                            )}

                            <MetaItem icon={<IconClock/>} label="Sent">
                                {fmt(message.created_at)}
                            </MetaItem>
                        </div>
                    </div>

                    {/* ── Targeting filters card (cat 1 only) ── */}
                    {hasFilters && !isClientCategory && (
                        <div className="bg-white border border-gray-200 p-5">
                            <SectionLabel>Targeting Filters</SectionLabel>
                            <div className="flex flex-col gap-4">

                                {/* Disciplines */}
                                {disciplineNames.length > 0 && (
                                    <div className="flex items-start gap-2.5">
                                        <span className="mt-0.5 shrink-0 text-gray-400"><IconFilter/></span>
                                        <div>
                                            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                                                Disciplines
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {disciplineNames.map(name => (
                                                    <span key={name} className="text-[12px] font-medium px-2.5 py-0.5 bg-violet-50 border border-violet-200 text-violet-700">
                                                        {name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Location */}
                                {(filters.city || filters.state) && (
                                    <div className="flex items-start gap-2.5">
                                        <span className="mt-0.5 shrink-0 text-gray-400"><IconLocation/></span>
                                        <div>
                                            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                                                Location
                                            </div>
                                            <span className="text-[13px] text-gray-700">
                                                {[filters.city, filters.state].filter(Boolean).join(', ')}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Excluded / Added counts */}
                                {(excludedCount > 0 || addedCount > 0) && (
                                    <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
                                        {excludedCount > 0 && (
                                            <span className="text-[12px] font-medium text-red-500 bg-red-50 border border-red-200 px-2.5 py-1">
                                                {excludedCount} excluded
                                            </span>
                                        )}
                                        {addedCount > 0 && (
                                            <span className="text-[12px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1">
                                                {addedCount} added outside filter
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Recipients card ── */}
                    {recipients.length > 0 && (
                        <div className="bg-white border border-gray-200">

                            {/* Card header */}
                            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                                <SectionLabel>Recipients</SectionLabel>
                                <div className="flex items-center gap-3 text-[12px]">
                                    <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"/>
                                        {message.read_count} read
                                    </span>
                                    <span className="text-gray-300">·</span>
                                    <span className="text-gray-400">{message.unread_count} unread</span>
                                </div>
                            </div>

                            {/* Rows */}
                            <div className="divide-y divide-gray-50">
                                {recipients.map((notif) => (
                                    <div
                                        key={notif.id}
                                        className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors"
                                    >
                                        <UserAvatar user={notif.user} size={32}/>
                                        <span className="flex-1 text-[13.5px] font-medium text-gray-800 min-w-0 truncate">
                                            {notif.user.name}
                                        </span>
                                        {notif.read_at ? (
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <svg width="13" height="13" fill="none" stroke="#10B981" strokeWidth="2.5" viewBox="0 0 24 24">
                                                    <polyline points="20,6 9,17 4,12"/>
                                                </svg>
                                                <span className="text-[12px] text-emerald-600 font-medium">Read</span>
                                                <span className="text-[12px] text-gray-400">{fmt(notif.read_at)}</span>
                                            </div>
                                        ) : (
                                            <span className="text-[11.5px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
                                                Unread
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Load More */}
                            {recipientsMeta.current_page < recipientsMeta.last_page && (
                                <div className="px-6 py-4 border-t border-gray-100 text-center">
                                    <button
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                        className="text-[13px] font-semibold text-indigo-600 hover:underline disabled:opacity-50"
                                    >
                                        {loadingMore ? 'Loading…' : 'Load More'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

export default AdminMessage